<template>
  <div class="space-y-6">
    <BaseDataIterator
      :service="fetchData"
      disable-search
      disable-filters
      :default-query-params="defaultQueryParams"
    >
      <template #before-pagination="{ queryContext }">
        <div class="px-4 py-3 flex gap-4">
          <button
            :class="[
              queryContext.actions.getQueryParam('filter') === null
                ? 'bg-blue-100 text-blue-700 px-3 py-2 font-medium text-sm rounded-md'
                : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md',
            ]"
            @click="
              () => {
                queryContext.actions.removeQueryParam('filter');
              }
            "
          >
            All <span class="font-normal text-xs">({{ emailAddressesCount }})</span>
          </button>
          <button
            :class="[
              queryContext.actions.getQueryParam('filter.status') === 'PENDING'
                ? 'bg-blue-100 text-blue-700 px-3 py-2 font-medium text-sm rounded-md'
                : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md',
            ]"
            @click="
              () => {
                queryContext.actions.removeQueryParam('filter');
                queryContext.actions.setQueryParam('filter.status', 'PENDING');
              }
            "
          >
            Pending <span class="font-normal text-xs">({{ recipientsPendingCount }})</span>
          </button>
          <button
            :class="[
              queryContext.actions.getQueryParam('filter.status') === 'SENT'
                ? 'bg-blue-100 text-blue-700 px-3 py-2 font-medium text-sm rounded-md'
                : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md',
            ]"
            @click="
              () => {
                queryContext.actions.removeQueryParam('filter');
                queryContext.actions.setQueryParam('filter.status', 'SENT');
              }
            "
          >
            Sent <span class="font-normal text-xs">({{ recipientsSentCount }})</span>
          </button>
          <button
            :class="[
              queryContext.actions.getQueryParam('filter.opened') === 1
                ? 'bg-blue-100 text-blue-700 px-3 py-2 font-medium text-sm rounded-md'
                : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md',
            ]"
            @click="
              () => {
                queryContext.actions.removeQueryParam('filter');
                queryContext.actions.setQueryParam('filter.opened', 1);
              }
            "
          >
            Opened <span class="font-normal text-xs">({{ uniqueOpenCount }})</span>
          </button>
          <button
            :class="[
              queryContext.actions.getQueryParam('filter.status') === 'FAILED'
                ? 'bg-blue-100 text-blue-700 px-3 py-2 font-medium text-sm rounded-md'
                : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md',
            ]"
            @click="
              () => {
                queryContext.actions.removeQueryParam('filter');
                queryContext.actions.setQueryParam('filter.status', 'FAILED');
              }
            "
          >
            Failed <span class="font-normal text-xs">({{ recipientsFailedCount }})</span>
          </button>
          <button
            :class="[
              queryContext.actions.getQueryParam('filter.unsubscribed') === 1
                ? 'bg-blue-100 text-blue-700 px-3 py-2 font-medium text-sm rounded-md'
                : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md',
            ]"
            @click="
              () => {
                queryContext.actions.removeQueryParam('filter');
                queryContext.actions.setQueryParam('filter.unsubscribed', 1);
              }
            "
          >
            Unsubscribed <span class="font-normal text-xs">({{ unsubscribedCount }})</span>
          </button>
          <button
            :class="[
              queryContext.actions.getQueryParam('filter.bounced') === 1
                ? 'bg-blue-100 text-blue-700 px-3 py-2 font-medium text-sm rounded-md'
                : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md',
            ]"
            @click="
              () => {
                queryContext.actions.removeQueryParam('filter');
                queryContext.actions.setQueryParam('filter.bounced', 1);
              }
            "
          >
            Bounced <span class="font-normal text-xs">({{ bouncedCount }})</span>
          </button>
        </div>
      </template>
      <template #default="dataIteratorProps">
        <BaseDataTable :data="dataIteratorProps.data">
          <template #header>
            <TrackingLogListHeader />
          </template>

          <template #default="dataTableProps">
            <TrackingLogListItem
              :tracking-log="dataTableProps.item"
              @change="dataIteratorProps.fetchList"
            />
          </template>
        </BaseDataTable>
      </template>
    </BaseDataIterator>
  </div>
</template>

<script>
import Numbro from '@/utils/numbro';
import { emailCampaignTrackingLogsClient } from '@/api/clients';
import TrackingLogListHeader from './TrackingLogListHeader.vue';
import TrackingLogListItem from './TrackingLogListItem.vue';

export default {
  components: {
    TrackingLogListHeader,
    TrackingLogListItem,
  },
  props: {
    emailCampaign: {
      type: Object,
      required: true,
    },
    defaultQueryParams: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    emailAddressesCount() {
      return Numbro(this.emailCampaign.email_addresses_count || 0).format({
        thousandSeparated: true,
      });
    },
    recipientsPendingCount() {
      return Numbro(this.emailCampaign.email_addresses_pending_count || 0).format({
        thousandSeparated: true,
      });
    },
    recipientsSentCount() {
      return Numbro(this.emailCampaign.email_addresses_sent_count || 0).format({
        thousandSeparated: true,
      });
    },
    recipientsFailedCount() {
      return Numbro(this.emailCampaign.email_addresses_failed_count || 0).format({
        thousandSeparated: true,
      });
    },
    uniqueOpenCount() {
      return Numbro(this.emailCampaign.unique_open_count || 0).format({
        thousandSeparated: true,
      });
    },
    unsubscribedCount() {
      return Numbro(this.emailCampaign.unsubscribed_count || 0).format({
        thousandSeparated: true,
      });
    },
    bouncedCount() {
      return Numbro(this.emailCampaign.bounced_count || 0).format({
        thousandSeparated: true,
      });
    },
  },
  methods: {
    fetchData(params = {}) {
      return emailCampaignTrackingLogsClient.list(this.emailCampaign.id, {
        ...params,
        include: 'contacts,open_logs_count,bounce_logs_count',
      });
    },
  },
};
</script>
